import { generateId, deepCloneNodes, calculateRect, measureNode, calculateLayout } from '../utils.js';

export class PrefabManager {
  // Migrate old prefabs that have PrefabRoot wrapper to new format
  static migratePrefab(prefabDef) {
    if (!prefabDef || !prefabDef.nodes || prefabDef.nodes.length === 0) return prefabDef;

    const root = prefabDef.nodes[0];

    // Check if this is an old-style prefab with PrefabRoot wrapper
    const isPrefabRoot = root.id?.startsWith('PrefabRoot-') ||
                         (root.label === 'Root' &&
                          root.anchors?.minX === 0 &&
                          root.anchors?.maxX === 1 &&
                          root.anchors?.minY === 0 &&
                          root.anchors?.maxY === 1 &&
                          root.x === 0 &&
                          root.y === 0 &&
                          root.width === 0 &&
                          root.height === 0);

    if (!isPrefabRoot) return prefabDef; // Already migrated or new format

    // Find the actual content node (child of PrefabRoot)
    const contentNode = prefabDef.nodes.find(n => n.parentId === root.id);
    if (!contentNode) return prefabDef; // No content node found, keep as is

    // Remove PrefabRoot and promote content node to root
    const newNodes = prefabDef.nodes
      .filter(n => n.id !== root.id) // Remove PrefabRoot
      .map(n => {
        if (n.id === contentNode.id) {
          // Promote content node to root
          return { ...n, parentId: undefined };
        }
        return n;
      });

    return {
      ...prefabDef,
      nodes: newNodes
    };
  }

  // Migrate all prefabs in an array
  static migratePrefabs(prefabs) {
    if (!Array.isArray(prefabs)) return prefabs;
    return prefabs.map(prefab => this.migratePrefab(prefab));
  }

  static createPrefab(selectedNodeIds, name, activeNodes, prefabs, rootW, rootH) {
    // 1. Validation: Single Root Selection Only
    const rootsInSelection = activeNodes.filter(n =>
      selectedNodeIds.includes(n.id) &&
      (!n.parentId || !selectedNodeIds.includes(n.parentId))
    );

    if (rootsInSelection.length !== 1) return null;

    const root = rootsInSelection[0];

    // 2. Deep Clone for Definition (1:1 Copy)
    // We clone the hierarchy exactly as is. The root of the clone becomes the root of the definition.
    let definitionNodes = [];
    let idMap = new Map(); // Map from Instance ID -> Definition ID
    const res = deepCloneNodes(activeNodes, root.id);

    if (res) {
      const { newNodes, newRootId, idMap: map } = res;
      idMap = map;
      const clonedRoot = newNodes.find(n => n.id === newRootId);

      if (clonedRoot) {
        // Prepare Definition Root
        // We reset x/y to 0 and anchors to Top-Left to ensure the prefab definition
        // is based at the origin (0,0) for isolation editing. 
        // The instance will retain the actual position and anchors in the scene.
        clonedRoot.parentId = undefined;
        clonedRoot.x = 0;
        clonedRoot.y = 0;
        clonedRoot.anchors = { minX: 0, maxX: 0, minY: 0, maxY: 0 };
        definitionNodes = newNodes;
      }
    }

    const prefabId = generateId('PrefabDef');
    const newPrefabDef = {
        id: prefabId,
        name,
        nodes: definitionNodes,
        defaultWidth: Math.round(root.width),
        defaultHeight: Math.round(root.height)
    };

    // 3. Instance Creation (In-Place Conversion)
    // We update the original nodes to include the prefab reference and source mapping
    
    // Find all nodes that are part of this new prefab structure (selected descendants)
    // We can use the keys of idMap which contains all IDs that were cloned
    const instanceNodes = [];
    const nodesToRemove = []; // We will technically "update" them, but replacing works too.

    for (const [instanceId, defId] of idMap.entries()) {
        const originalNode = activeNodes.find(n => n.id === instanceId);
        if (originalNode) {
            nodesToRemove.push(instanceId);
            
            const isRoot = instanceId === root.id;
            const updatedNode = {
                ...originalNode,
                props: {
                    ...originalNode.props,
                    sourceNodeId: defId, // Link to definition node
                    ...(isRoot ? { prefabId } : {}) // Only root gets prefabId
                }
            };
            instanceNodes.push(updatedNode);
        }
    }

    return { 
        prefabDef: newPrefabDef, 
        instance: instanceNodes[0], // Backward compat if needed, but we should use instanceNodes
        instanceNodes,
        nodesToRemove 
    };
  }

  /**
   * Helper to clean up instanceOverrides array by removing invalid references
   * @param {Array<string>} instanceOverrides - Array of override paths
   * @param {Array} defNodes - Definition nodes to validate against
   * @returns {Array<string>} Cleaned array of valid overrides
   */
  static cleanupInstanceOverrides(instanceOverrides, defNodes) {
    if (!instanceOverrides || instanceOverrides.length === 0) return [];

    // Build a set of valid sourceNodeIds from definition
    const validSourceIds = new Set(defNodes.map(n => n.id));

    // Filter overrides to only keep ones with valid sourceNodeIds
    return instanceOverrides.filter(override => {
      const sourceNodeId = override.split('.')[0];
      return validSourceIds.has(sourceNodeId);
    });
  }

  static detachPrefab(instanceId, activeNodes, prefabs) {
    const instance = activeNodes.find(n => n.id === instanceId);
    if (!instance || !instance.props?.prefabId) return null;

    const def = prefabs.find(p => p.id === instance.props.prefabId);
    if (!def) return null;

    // Just remove the prefabId, sourceNodeId, and instanceOverrides from all descendants
    // The nodes are already in the scene, so we just strip metadata.

    // Find all descendants that belong to this instance
    const descendants = [];
    const queue = [instanceId];
    while(queue.length > 0) {
        const pid = queue.shift();
        descendants.push(pid);
        const children = activeNodes.filter(n => n.parentId === pid);
        children.forEach(c => queue.push(c.id));
    }

    const newNodes = descendants.map(id => {
        const node = activeNodes.find(n => n.id === id);
        const { prefabId, sourceNodeId, instanceOverrides, ...otherProps } = node.props || {};
        return {
            ...node,
            props: otherProps
        };
    });

    return { newNodes, newRootId: instanceId, instanceId };
  }

  static updatePrefabFromInstance(instanceId, activeNodes, prefabs) {
    const instance = activeNodes.find(n => n.id === instanceId);
    if (!instance || !instance.props?.prefabId) return null;

    const def = prefabs.find(p => p.id === instance.props.prefabId);
    if (!def) return null;

    const newDefNodes = [...def.nodes];
    const { prefabId, data, overrides, ...propsToSync } = instance.props;
    
    // 1. Sync Root Props (exclude overrides from root props)
    newDefNodes[0] = {
      ...newDefNodes[0],
      props: { ...newDefNodes[0].props, ...propsToSync },
      attachedComponents: instance.attachedComponents
    };

    // 2. Apply Overrides to Definition Nodes
    if (overrides) {
        Object.entries(overrides).forEach(([internalId, override]) => {
            const defNodeIndex = newDefNodes.findIndex(n => n.id === internalId);
            if (defNodeIndex === -1) return;

            newDefNodes[defNodeIndex] = this.applyOverrideToDefNode(newDefNodes[defNodeIndex], override);
        });
    }

    return { prefabId: def.id, updatedNodes: newDefNodes };
  }

  /**
   * Apply override to a single definition node
   * @param {Object} defNode - Definition node to update
   * @param {Object} override - Override object with props
   * @returns {Object} Updated node
   */
  static applyOverrideToDefNode(defNode, override) {
      const newProps = { ...defNode.props, ...(override.props || {}) };

      // Clean undefined properties
      Object.keys(newProps).forEach(key => {
          if (newProps[key] === undefined) delete newProps[key];
      });

      return {
          ...defNode,
          props: newProps
      };
  }

  /**
   * Helper to check if a property path should be skipped during sync
   * @param {string} propertyPath - Dot notation path like "props.backgroundColor" or "width"
   * @param {Array<string>} instanceOverrides - Array of override paths like ["props.backgroundColor", "props.responsiveOverrides.default.backgroundColor"]
   */
  static shouldSkipProperty(propertyPath, instanceOverrides) {
      if (!instanceOverrides || instanceOverrides.length === 0) return false;

      // Check exact match
      if (instanceOverrides.includes(propertyPath)) return true;

      // Check if any override path starts with this path (handles nested properties)
      // e.g., if checking "props.responsiveOverrides" and we have "props.responsiveOverrides.default.backgroundColor"
      return instanceOverrides.some(override => override.startsWith(propertyPath + '.'));
  }

  /**
   * Deep merge responsiveOverrides, preserving user-modified breakpoint properties
   * @param {Object} defResponsive - Definition's responsive overrides
   * @param {Object} instanceResponsive - Instance's responsive overrides
   * @param {Array<string>} childInstanceOverrides - Array of override paths
   * @returns {Object} Merged responsive overrides
   */
  static mergeResponsiveOverrides(defResponsive, instanceResponsive, childInstanceOverrides) {
      const merged = { ...defResponsive };

      Object.keys(instanceResponsive).forEach(bp => {
          const defBpProps = defResponsive[bp] || {};
          const instanceBpProps = instanceResponsive[bp] || {};
          const mergedBp = { ...defBpProps };

          Object.keys(instanceBpProps).forEach(prop => {
              const specificPath = `props.responsiveOverrides.${bp}.${prop}`;
              if (childInstanceOverrides.includes(specificPath)) {
                  mergedBp[prop] = instanceBpProps[prop];
              }
          });

          merged[bp] = mergedBp;
      });

      return merged;
  }

  /**
   * Apply instance overrides to a single property in mergedProps
   * @param {string} key - Property key
   * @param {Object} mergedProps - Props object to modify
   * @param {Object} instanceNode - Instance node with props
   * @param {Object} instanceOverrides - Instance overrides object
   * @param {Array<string>} childInstanceOverrides - Array of override paths
   */
  static applyPropertyOverride(key, mergedProps, instanceNode, instanceOverrides, childInstanceOverrides) {
      // Handle responsiveOverrides with deep merge
      if (key === 'responsiveOverrides' && typeof instanceNode.props?.[key] === 'object') {
          const defResponsive = mergedProps[key] || {};
          const instanceResponsive = instanceNode.props[key] || {};
          mergedProps[key] = this.mergeResponsiveOverrides(defResponsive, instanceResponsive, childInstanceOverrides);
          return;
      }

      // Use instance value if available
      if (instanceNode.props?.[key] !== undefined) {
          mergedProps[key] = instanceNode.props[key];
          return;
      }

      // Fallback to instanceOverrides
      if (instanceOverrides[key] !== undefined) {
          mergedProps[key] = instanceOverrides[key];
      }
  }

  /**
   * Build map of instance nodes by sourceNodeId
   * @param {Object} instanceRoot - Root node of the instance
   * @param {Object} defRoot - Root node of the definition
   * @param {Array} allNodes - All nodes in the scene
   * @returns {Object} { instanceNodesMap, instanceNodesList }
   */
  static buildInstanceNodesMap(instanceRoot, defRoot, allNodes) {
      const instanceNodesMap = new Map();
      const instanceNodesList = [];

      const queue = [instanceRoot];
      while (queue.length > 0) {
          const node = queue.shift();
          instanceNodesList.push(node);

          if (node.props?.sourceNodeId) {
              instanceNodesMap.set(node.props.sourceNodeId, node);
          } else if (node.id === instanceRoot.id) {
              instanceNodesMap.set(defRoot.id, node);
          }

          const children = allNodes.filter(n => n.parentId === node.id && n.props?.sourceNodeId);
          children.forEach(c => queue.push(c));
      }

      return { instanceNodesMap, instanceNodesList };
  }

  /**
   * Sync a child node (non-root) from definition to instance
   * @param {Object} defNode - Definition node
   * @param {Object} instanceNode - Instance node
   * @param {Map} instanceNodesMap - Map of instance nodes
   * @returns {Object} Updated node
   */
  static syncChildNode(defNode, instanceNode, instanceNodesMap) {
      const updatedNode = { ...instanceNode };

      // Sync parent relationship
      const defParentId = defNode.parentId;
      const instanceParent = instanceNodesMap.get(defParentId);
      if (instanceParent) {
          updatedNode.parentId = instanceParent.id;
      }

      updatedNode.type = defNode.type;

      // Get instance-level overrides for this specific child node
      const childInstanceOverrides = instanceNode.props?.instanceOverrides || [];

      // Only update layout properties if not overridden by user
      const layoutProps = ['width', 'height', 'x', 'y', 'anchors', 'label'];
      layoutProps.forEach(prop => {
          if (!this.shouldSkipProperty(prop, childInstanceOverrides)) {
              updatedNode[prop] = defNode[prop];
          }
      });

      // Merge props: Start with def props, selectively skip user-overridden properties
      const instanceOverrides = instanceNode.props?.overrides || {};
      const mergedProps = { ...defNode.props };

      // Apply instanceOverrides protection - skip properties that user has changed
      Object.keys(mergedProps).forEach(key => {
          if (!this.shouldSkipProperty(`props.${key}`, childInstanceOverrides)) return;
          this.applyPropertyOverride(key, mergedProps, instanceNode, instanceOverrides, childInstanceOverrides);
      });

      updatedNode.props = {
          ...mergedProps,
          ...instanceOverrides,
          sourceNodeId: defNode.id,
          overrides: instanceOverrides,
          instanceOverrides: childInstanceOverrides.length > 0 ? childInstanceOverrides : undefined
      };

      if (updatedNode.props.instanceOverrides === undefined) {
          delete updatedNode.props.instanceOverrides;
      }

      updatedNode.attachedComponents = defNode.attachedComponents;

      return updatedNode;
  }

  /**
   * Sync root node from definition to instance
   * @param {Object} defNode - Definition node
   * @param {Object} instanceNode - Instance node
   * @returns {Object} Updated node
   */
  static syncRootNode(defNode, instanceNode) {
      const updatedNode = { ...instanceNode };
      updatedNode.attachedComponents = defNode.attachedComponents;

      const { x, y, width, height, anchors, prefabId, ...defProps } = defNode.props || {};
      const { prefabId: pId, sourceNodeId: sId, overrides, instanceOverrides: rootInstanceOverrides } = updatedNode.props || {};
      const instanceOverridesObj = overrides || {};
      const rootChildOverrides = rootInstanceOverrides || [];

      // Apply selective merge for root props
      const mergedProps = { ...defProps };
      Object.keys(mergedProps).forEach(key => {
          if (!this.shouldSkipProperty(`props.${key}`, rootChildOverrides)) return;
          if (updatedNode.props?.[key] !== undefined) {
              mergedProps[key] = updatedNode.props[key];
          }
      });

      updatedNode.props = {
          ...mergedProps,
          ...instanceOverridesObj,
          prefabId: pId,
          sourceNodeId: sId,
          overrides: instanceOverridesObj,
          instanceOverrides: rootChildOverrides.length > 0 ? rootChildOverrides : undefined
      };

      if (updatedNode.props.instanceOverrides === undefined) {
          delete updatedNode.props.instanceOverrides;
      }

      return updatedNode;
  }

  /**
   * Sync a single instance with the prefab definition
   * @param {Object} instanceRoot - Root node of the instance
   * @param {Object} prefabDef - Prefab definition
   * @param {Object} defRoot - Root node of the definition
   * @param {Array} allNodes - All nodes in the scene
   * @returns {Object} { nodesToAdd, nodesToUpdate, nodesToRemove }
   */
  static syncSingleInstance(instanceRoot, prefabDef, defRoot, allNodes) {
      const nodesToAdd = [];
      const nodesToUpdate = [];
      const nodesToRemove = [];

      // 1. Build instance nodes map
      const { instanceNodesMap, instanceNodesList } = this.buildInstanceNodesMap(instanceRoot, defRoot, allNodes);

      // 2. Sync definition nodes to instance
      prefabDef.nodes.forEach(defNode => {
          const isRoot = defNode.id === defRoot.id;
          const instanceNode = instanceNodesMap.get(defNode.id);

          if (instanceNode) {
              // Update existing node
              const updatedNode = isRoot
                  ? this.syncRootNode(defNode, instanceNode)
                  : this.syncChildNode(defNode, instanceNode, instanceNodesMap);

              nodesToUpdate.push(updatedNode);
          } else {
              // Create new node (added to prefab definition)
              const defParentId = defNode.parentId;
              const instanceParent = instanceNodesMap.get(defParentId);

              if (instanceParent) {
                  const newNodeId = generateId(defNode.type);
                  const newNode = {
                      ...defNode,
                      id: newNodeId,
                      parentId: instanceParent.id,
                      props: {
                          ...defNode.props,
                          sourceNodeId: defNode.id
                      }
                  };
                  nodesToAdd.push(newNode);
                  instanceNodesMap.set(defNode.id, newNode);
              }
          }
      });

      // 3. Find deleted nodes (exist in instance but not in definition)
      instanceNodesList.forEach(node => {
          if (node.id !== instanceRoot.id && node.props?.sourceNodeId) {
              const defNode = prefabDef.nodes.find(n => n.id === node.props.sourceNodeId);
              if (!defNode) {
                  nodesToRemove.push(node.id);
              }
          }
      });

      return { nodesToAdd, nodesToUpdate, nodesToRemove };
  }

  /**
   * Synchronizes all instances of a prefab in the scene with the updated definition.
   * Handles adding, removing, and updating child nodes while preserving instance position/parent.
   * Respects instanceOverrides to prevent overwriting user-modified properties.
   */
  static syncInstances(prefabDef, allNodes) {
      if (!prefabDef || !allNodes) return { nodesToAdd: [], nodesToUpdate: [], nodesToRemove: [] };

      const defRoot = prefabDef.nodes[0];
      const instances = allNodes.filter(n => n.props?.prefabId === prefabDef.id);

      let nodesToAdd = [];
      let nodesToUpdate = [];
      let nodesToRemove = [];

      instances.forEach(instanceRoot => {
          const result = this.syncSingleInstance(instanceRoot, prefabDef, defRoot, allNodes);
          nodesToAdd.push(...result.nodesToAdd);
          nodesToUpdate.push(...result.nodesToUpdate);
          nodesToRemove.push(...result.nodesToRemove);
      });

      return { nodesToAdd, nodesToUpdate, nodesToRemove };
  }
}